import { Box, Typography, Switch, FormControlLabel, TextField, Button, Chip, Stack, IconButton, Divider, Autocomplete, Paper } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import { useState, useEffect } from "react";
import API from "../../../api";
import { startToast, updateToastError, updateToastSuccess } from "../../../utils/notification-loading";

// ── Static maps ───────────────────────────────────────────────────────────────

const PROTOCOL_LABELS = {
  ads: 'ADS', amqp: 'AMQP', bacnet: 'BACnet', c37118: 'C37.118',
  codesys: 'CODESYS', dds: 'DDS', dlms: 'DLMS', dnp3: 'DNP3',
  dnp3_sa: 'DNP3 SA', enip_cip: 'EtherNet/IP CIP', fins: 'FINS',
  ge: 'GE SRTP', hart_ip: 'HART-IP', iccp: 'ICCP', iec104: 'IEC 104',
  iec_mms: 'IEC MMS', knxip: 'KNX/IP', melsec: 'MELSEC', modbus: 'Modbus',
  modbus_tls: 'Modbus TLS', mqtt: 'MQTT', opcua: 'OPC-UA',
  opcua_pubsub: 'OPC-UA PubSub', pccc: 'PCCC', profinet: 'PROFINET',
  ptp: 'PTP', s7tcp: 'S7 TCP', sparkplug: 'Sparkplug', tsaa: 'TSAA',
  umas: 'UMAS', ur: 'UR',
};

// type 'array' → comma-separated text; type 'number' → numeric input
const PORT_FIELDS = {
  ads:         [{ key: 'ads_ports',           label: 'Ports',          type: 'array'  }],
  amqp:        [{ key: 'amqp_ports',          label: 'Ports',          type: 'array'  }],
  bacnet:      [{ key: 'bacnet_ports',         label: 'Ports',          type: 'array'  }],
  c37118:      [{ key: 'c37118_ports',         label: 'Ports',          type: 'array'  }],
  codesys:     [{ key: 'codesys_ports',        label: 'Ports',          type: 'array'  }],
  dds: [
    { key: 'dds_ports',          label: 'Ports',          type: 'array'  },
    { key: 'dds_base_port',      label: 'Base Port',      type: 'number' },
    { key: 'dds_port_range_max', label: 'Port Range Max', type: 'number' },
  ],
  dlms:        [{ key: 'dlms_ports',           label: 'Ports',          type: 'array'  }],
  dnp3:        [{ key: 'dnp3_ports',           label: 'Ports',          type: 'array'  }],
  enip_cip: [
    { key: 'enip_tcp_ports',     label: 'TCP Ports',      type: 'array'  },
    { key: 'enip_udp_ports',     label: 'UDP Ports',      type: 'array'  },
  ],
  fins: [
    { key: 'fins_tcp_ports',     label: 'TCP Ports',      type: 'array'  },
    { key: 'fins_udp_ports',     label: 'UDP Ports',      type: 'array'  },
  ],
  ge: [
    { key: 'ge_srtp_ports',      label: 'SRTP Ports',     type: 'array'  },
    { key: 'ge_egd_ports',       label: 'EGD Ports',      type: 'array'  },
  ],
  hart_ip:     [{ key: 'hart_ip_ports',        label: 'Ports',          type: 'array'  }],
  iec104:      [{ key: 'iec104_ports',          label: 'Ports',          type: 'array'  }],
  iec_mms:     [{ key: 'iec_mms_ports',         label: 'Ports',          type: 'array'  }],
  knxip:       [{ key: 'knxip_ports',           label: 'Ports',          type: 'array'  }],
  melsec: [
    { key: 'melsec_tcp_ports',   label: 'TCP Ports',      type: 'array'  },
    { key: 'melsec_udp_ports',   label: 'UDP Ports',      type: 'array'  },
  ],
  modbus:      [{ key: 'modbus_ports',          label: 'Ports',          type: 'array'  }],
  modbus_tls:  [{ key: 'modbus_tls_ports',      label: 'Ports',          type: 'array'  }],
  mqtt:        [{ key: 'mqtt_ports',            label: 'Ports',          type: 'array'  }],
  opcua:       [{ key: 'opcua_ports',           label: 'Ports',          type: 'array'  }],
  opcua_pubsub:[{ key: 'opcua_pubsub_ports',    label: 'Ports',          type: 'array'  }],
  profinet:    [{ key: 'profinet_ports',        label: 'Ports',          type: 'array'  }],
  ptp:         [{ key: 'ptp_ports',             label: 'Ports',          type: 'array'  }],
  s7tcp:       [{ key: 's7tcp_ports',           label: 'Ports',          type: 'array'  }],
  tsaa:        [{ key: 'tsaa_ports',            label: 'Ports',          type: 'array'  }],
  ur: [
    { key: 'ur_rtde_ports',      label: 'RTDE Ports',     type: 'array'  },
    { key: 'ur_primary_ports',   label: 'Primary Ports',  type: 'array'  },
    { key: 'ur_dashboard_ports', label: 'Dashboard Ports',type: 'array'  },
  ],
};

const DEFAULT_CONFIG = {
  global_options: { log_enabled: false, broker_enabled: true },
  protocols_enabled: {
    ads: true, amqp: false, bacnet: true, c37118: false, codesys: true,
    dds: false, dlms: false, dnp3: true, dnp3_sa: true, enip_cip: true,
    fins: true, ge: true, hart_ip: false, iccp: true, iec104: true,
    iec_mms: true, knxip: false, melsec: false, modbus: true, modbus_tls: true,
    mqtt: true, opcua: true, opcua_pubsub: true, pccc: true, profinet: true,
    ptp: false, s7tcp: true, sparkplug: false, tsaa: false, umas: true, ur: false,
  },
  ports: {
    ads_ports: ['48898/tcp', '6030/tcp'],
    amqp_ports: ['5672/tcp', '5671/tcp'],
    bacnet_ports: ['47808/udp'],
    c37118_ports: ['4712/tcp', '4713/tcp'],
    codesys_ports: ['11740/tcp', '1217/tcp'],
    dds_ports: ['7400/udp', '7401/udp'],
    dds_base_port: 7400,
    dds_port_range_max: 7900,
    dlms_ports: ['4059/tcp'],
    dnp3_ports: ['20000/tcp'],
    enip_tcp_ports: ['44818/tcp'],
    enip_udp_ports: ['2222/udp', '44818/udp'],
    fins_tcp_ports: ['9600/tcp'],
    fins_udp_ports: ['9600/udp'],
    ge_srtp_ports: ['18245/tcp'],
    ge_egd_ports: ['18246/udp'],
    hart_ip_ports: ['5094/tcp', '5094/udp'],
    iec104_ports: ['2404/tcp'],
    iec_mms_ports: ['102/tcp'],
    knxip_ports: ['3671/udp'],
    melsec_tcp_ports: ['5000/tcp', '5001/tcp', '5002/tcp', '5010/tcp', '5014/tcp', '6000/tcp', '6001/tcp', '6002/tcp', '6010/tcp'],
    melsec_udp_ports: ['5000/udp', '5001/udp', '5002/udp', '5010/udp', '5014/udp', '6000/udp', '6001/udp', '6002/udp', '6010/udp'],
    modbus_ports: ['502/tcp'],
    modbus_tls_ports: ['802/tcp'],
    mqtt_ports: ['1883/tcp', '8883/tcp'],
    opcua_ports: ['4840/tcp'],
    opcua_pubsub_ports: ['4840/udp', '4841/udp'],
    profinet_ports: ['34964/udp'],
    ptp_ports: ['319/udp', '320/udp'],
    s7tcp_ports: ['102/tcp'],
    tsaa_ports: ['1502/tcp'],
    ur_rtde_ports: ['30004/tcp'],
    ur_primary_ports: ['30001/tcp', '30002/tcp'],
    ur_dashboard_ports: ['29999/tcp'],
  },
  filtering: {
    restrict_to_targets: true,
    monitored_hosts: [],
    monitored_subnets: [],
  },
  device_swap: [],
};

// ── Sidebar sections ──────────────────────────────────────────────────────────

const SECTIONS = [
  {
    id:          'configuration',
    label:       'Configuration',
    description: 'Protocols, ports and traffic filtering',
    icon:        SettingsEthernetIcon,
  },
];

// ── Shared styles ─────────────────────────────────────────────────────────────

const cardSx = {
  backgroundColor: 'rgba(69, 144, 149, 0.05)',
  borderRadius: 2,
  border: '1px solid rgba(69, 144, 149, 0.1)',
  p: 3,
};

const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#449095' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#449095' },
};

// ── Validation ────────────────────────────────────────────────────────────────

const IPV4_RE = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
const isValidIPv4 = (s) => { const m = s.match(IPV4_RE); return m ? m.slice(1).every((n) => Number(n) <= 255) : false; };
const IPV6_RE = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:(:[0-9a-fA-F]{1,4}){1,6}|:(:[0-9a-fA-F]{1,4}){1,7}|::)$/;
const isValidIPv6 = (s) => IPV6_RE.test(s);
const isValidIP   = (s) => isValidIPv4(s) || isValidIPv6(s);
const isValidCIDR = (s) => {
  const slash = s.lastIndexOf('/');
  if (slash === -1) return false;
  const addr   = s.slice(0, slash);
  const prefix = Number(s.slice(slash + 1));
  if (!Number.isInteger(prefix) || s.slice(slash + 1) === '') return false;
  if (isValidIPv4(addr)) return prefix >= 0 && prefix <= 32;
  if (isValidIPv6(addr)) return prefix >= 0 && prefix <= 128;
  return false;
};
// ── Config merge ──────────────────────────────────────────────────────────────

const mergeWithSystemDefaults = (deviceConfig, systemConfig) => {
  if (!deviceConfig && !systemConfig) return DEFAULT_CONFIG;
  const sysNewProtos = Object.fromEntries(
    Object.entries(systemConfig?.protocols_enabled ?? {}).filter(([k]) => !(k in DEFAULT_CONFIG.protocols_enabled))
  );
  const sysNewPorts = Object.fromEntries(
    Object.entries(systemConfig?.ports ?? {}).filter(([k]) => {
      const protoKey = k.replace(/_ports$/, '');
      return !(protoKey in DEFAULT_CONFIG.protocols_enabled);
    })
  );
  return {
    ...DEFAULT_CONFIG,
    ...(deviceConfig ?? {}),
    protocols_enabled: {
      ...DEFAULT_CONFIG.protocols_enabled,
      ...sysNewProtos,
      ...(deviceConfig?.protocols_enabled ?? {}),
    },
    ports: {
      ...DEFAULT_CONFIG.ports,
      ...sysNewPorts,
      ...(deviceConfig?.ports ?? {}),
    },
    filtering: {
      ...DEFAULT_CONFIG.filtering,
      ...(systemConfig?.filtering ?? {}),
      ...(deviceConfig?.filtering ?? {}),
    },
  };
};

const buildPortTexts = (ports) => {
  const texts = {};
  Object.entries(ports ?? {}).forEach(([key, val]) => { if (Array.isArray(val)) texts[key] = val.join(', '); });
  return texts;
};

const getDefaultPortExample = (field, ports = DEFAULT_CONFIG.ports) => {
  const defaultVal = ports[field.key] ?? DEFAULT_CONFIG.ports[field.key];
  if (field.type === 'array') return Array.isArray(defaultVal) && defaultVal.length > 0 ? defaultVal.join(', ') : 'No default values';
  return defaultVal !== undefined && defaultVal !== null ? String(defaultVal) : 'No default value';
};

// ── Sidebar item ──────────────────────────────────────────────────────────────

const SidebarItem = ({ section, selected, onClick }) => {
  const Icon = section.icon;
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2, py: 1.25, cursor: 'pointer', borderRadius: 1,
        backgroundColor: selected ? 'rgba(69,144,149,0.14)' : 'transparent',
        borderLeft: selected ? '3px solid #449095' : '3px solid transparent',
        transition: 'all 0.15s',
        '&:hover': { backgroundColor: selected ? 'rgba(69,144,149,0.14)' : 'rgba(255,255,255,0.04)' },
      }}
    >
      <Icon sx={{ fontSize: 20, color: selected ? '#449095' : '#666', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: selected ? 700 : 500, color: selected ? '#e8e8e8' : '#aaa', lineHeight: 1.3 }}>
          {section.label}
        </Typography>
        <Typography sx={{ fontSize: '0.68rem', color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {section.description}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Section: Configuration ────────────────────────────────────────────────────

const ConfigurationSection = ({ config, portTexts, selectedProtocol, setSelectedProtocol, setProtocol, setFiltering, handlePortTextChange, handlePortNumberChange, effectiveDefault, storedOtSettings, systemOtSettings, onSave }) => {
  const [newHost,    setNewHost]    = useState('');
  const [newSubnet,  setNewSubnet]  = useState('');
  const [hostErr,    setHostErr]    = useState('');
  const [subnetErr,  setSubnetErr]  = useState('');

  const resolvePortFields = (key) => {
    if (PORT_FIELDS[key]) return PORT_FIELDS[key];
    const portKey = `${key}_ports`;
    if (config.ports[portKey] !== undefined || systemOtSettings?.ports?.[portKey] !== undefined)
      return [{ key: portKey, label: 'Ports', type: 'array' }];
    return [];
  };

  const protocolEntries = Object.entries(config.protocols_enabled).sort(([a], [b]) => {
    const lA = PROTOCOL_LABELS[a] ?? systemOtSettings?.protocol_labels?.[a] ?? a;
    const lB = PROTOCOL_LABELS[b] ?? systemOtSettings?.protocol_labels?.[b] ?? b;
    return lA.localeCompare(lB);
  });
  const enabledCount     = protocolEntries.filter(([, e]) => e).length;
  const selectedPortFields = resolvePortFields(selectedProtocol);
  const selectedEnabled    = !!config.protocols_enabled[selectedProtocol];

  const addHost = () => {
    const v = newHost.trim();
    if (!v) return;
    if (!isValidIP(v)) { setHostErr('Invalid IP address (IPv4 or IPv6)'); return; }
    setFiltering('monitored_hosts', [...(config.filtering.monitored_hosts ?? []), v]);
    setNewHost(''); setHostErr('');
  };

  const addSubnet = () => {
    const v = newSubnet.trim();
    if (!v) return;
    if (!isValidCIDR(v)) { setSubnetErr('Invalid CIDR notation (e.g. 192.168.1.0/24)'); return; }
    setFiltering('monitored_subnets', [...(config.filtering.monitored_subnets ?? []), v]);
    setNewSubnet(''); setSubnetErr('');
  };

  return (
    <Stack spacing={3}>

      {/* ── Protocols & Ports ── */}
      <Box sx={cardSx}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#449095', mb: 2 }}>Protocols & Ports</Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2.5 }}>
          Select a protocol, enable or disable it, and configure its port settings. Protocols marked as <em>new</em> come from the device system configuration.
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip
            size="small"
            label={`${enabledCount} active of ${protocolEntries.length}`}
            sx={{ bgcolor: 'rgba(69,144,149,0.2)', color: '#d9fbfd', border: '1px solid rgba(69,144,149,0.35)', fontWeight: 600 }}
          />
        </Stack>

        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
            <Autocomplete
              size="small"
              disableClearable
              options={protocolEntries}
              getOptionLabel={([key]) => PROTOCOL_LABELS[key] ?? systemOtSettings?.protocol_labels?.[key] ?? key}
              value={protocolEntries.find(([key]) => key === selectedProtocol) ?? null}
              onChange={(_, entry) => entry && setSelectedProtocol(entry[0])}
              isOptionEqualToValue={([a], [b]) => a === b}
              sx={{ width: { xs: '100%', sm: 320 } }}
              slotProps={{
                paper: { sx: { bgcolor: '#0d1821', border: '1px solid rgba(69,144,149,0.25)', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' } },
                listbox: { sx: { p: 0.5 } },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Protocol"
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(7,16,24,0.35)', borderRadius: 1.5 } }}
                />
              )}
              renderOption={(props, [key, enabled]) => {
                const { key: _k, ...liProps } = props;
                const label  = PROTOCOL_LABELS[key] ?? systemOtSettings?.protocol_labels?.[key] ?? key;
                const isNew  = !(key in (storedOtSettings?.protocols_enabled ?? DEFAULT_CONFIG.protocols_enabled));
                return (
                  <Box component="li" key={key} {...liProps} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderRadius: 1, mx: 0.5, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(69,144,149,0.1)' }, '&[aria-selected="true"]': { bgcolor: 'rgba(69,144,149,0.18) !important' } }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: enabled ? '#449095' : 'transparent', border: enabled ? '2px solid #449095' : '1.5px solid rgba(255,255,255,0.22)', boxShadow: enabled ? '0 0 5px rgba(68,144,149,0.7)' : 'none' }} />
                    <Typography sx={{ flex: 1, fontSize: '0.85rem', color: enabled ? '#e8fafb' : 'rgba(255,255,255,0.42)', fontWeight: enabled ? 500 : 400 }}>{label}</Typography>
                    {isNew && <Chip label="new" size="small" sx={{ height: 17, fontSize: '0.6rem', fontWeight: 700, bgcolor: 'rgba(69,144,149,0.22)', color: '#7de8ed', border: '1px solid rgba(69,144,149,0.45)', '& .MuiChip-label': { px: 0.7 } }} />}
                  </Box>
                );
              }}
            />
            <FormControlLabel
              sx={{ m: 0, pb: { xs: 0, sm: 0.5 } }}
              label={selectedEnabled ? 'Enabled' : 'Disabled'}
              control={<Switch checked={selectedEnabled} onChange={(e) => setProtocol(selectedProtocol, e.target.checked)} sx={switchSx} />}
            />
          </Stack>

          {selectedPortFields.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
              No configurable port fields for this protocol.
            </Typography>
          ) : (
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {selectedPortFields.map((field) => (
                <TextField
                  key={field.key}
                  label={field.label}
                  size="small"
                  placeholder={getDefaultPortExample(field, effectiveDefault.ports)}
                  type={field.type === 'number' ? 'number' : 'text'}
                  value={field.type === 'array' ? (portTexts[field.key] ?? '') : (config.ports[field.key] ?? '')}
                  onChange={(e) => field.type === 'array' ? handlePortTextChange(field.key, e.target.value) : handlePortNumberChange(field.key, Number(e.target.value))}
                  helperText={`Default: ${getDefaultPortExample(field, effectiveDefault.ports)}`}
                  sx={{ minWidth: 220, flex: 1 }}
                  slotProps={{ input: { style: { fontSize: '0.85rem' } } }}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Box>

      {/* ── Filtering ── */}
      <Box sx={cardSx}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#449095', mb: 2 }}>Filtering</Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 2.5 }}>
          Control which hosts and subnets the OT engine should monitor. When <strong>Restrict to Targets</strong> is on, only traffic to or from the listed hosts and subnets is analyzed.
        </Typography>

        <FormControlLabel
          label="Restrict to Targets"
          control={<Switch checked={config.filtering.restrict_to_targets} onChange={(e) => setFiltering('restrict_to_targets', e.target.checked)} sx={switchSx} />}
        />

        <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.07)' }} />

        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Monitored Hosts</Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mb: 1.5 }}>
          Single IP addresses (IPv4 or IPv6) to include in OT monitoring.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="flex-start">
          <TextField
            size="small" value={newHost}
            onChange={(e) => { setNewHost(e.target.value); setHostErr(''); }}
            label="IP Address" placeholder="e.g. 192.168.1.10"
            onKeyDown={(e) => e.key === 'Enter' && addHost()}
            error={!!hostErr} helperText={hostErr || 'IPv4 or IPv6'}
            sx={{ flex: 1 }}
          />
          <IconButton onClick={addHost} sx={{ color: '#449095', mt: 0.5 }}><AddIcon /></IconButton>
        </Stack>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3, minHeight: 28 }}>
          {(config.filtering.monitored_hosts ?? []).map((h, i) => (
            <Chip key={i} label={h} size="small"
              onDelete={() => setFiltering('monitored_hosts', config.filtering.monitored_hosts.filter((_, j) => j !== i))}
              sx={{ fontFamily: 'monospace' }}
            />
          ))}
        </Box>

        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Monitored Subnets</Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mb: 1.5 }}>
          Subnets in CIDR notation (IPv4 or IPv6) to include in OT monitoring.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="flex-start">
          <TextField
            size="small" value={newSubnet}
            onChange={(e) => { setNewSubnet(e.target.value); setSubnetErr(''); }}
            label="CIDR" placeholder="e.g. 192.168.1.0/24"
            onKeyDown={(e) => e.key === 'Enter' && addSubnet()}
            error={!!subnetErr} helperText={subnetErr || 'IPv4 or IPv6 CIDR notation'}
            sx={{ flex: 1 }}
          />
          <IconButton onClick={addSubnet} sx={{ color: '#449095', mt: 0.5 }}><AddIcon /></IconButton>
        </Stack>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minHeight: 28 }}>
          {(config.filtering.monitored_subnets ?? []).map((s, i) => (
            <Chip key={i} label={s} size="small"
              onDelete={() => setFiltering('monitored_subnets', config.filtering.monitored_subnets.filter((_, j) => j !== i))}
              sx={{ fontFamily: 'monospace' }}
            />
          ))}
        </Box>
      </Box>

      <Box>
        <Button
          variant="contained"
          onClick={onSave}
          sx={{ backgroundColor: '#449095', color: '#fff', fontWeight: 700, px: 4, '&:hover': { backgroundColor: '#3a7a7e' } }}
        >
          Save Configuration
        </Button>
      </Box>
    </Stack>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const OtSettings = ({ storedOtSettings, systemOtSettings, embedded = false }) => {
  const effectiveDefault = mergeWithSystemDefaults(storedOtSettings, systemOtSettings) ?? DEFAULT_CONFIG;

  const [selectedId, setSelectedId]         = useState('configuration');
  const [config, setConfig]                 = useState(effectiveDefault);
  const [portTexts, setPortTexts]           = useState(() => buildPortTexts(effectiveDefault.ports ?? {}));
  const [selectedProtocol, setSelectedProtocol] = useState('ads');

  useEffect(() => {
    const merged = mergeWithSystemDefaults(storedOtSettings, systemOtSettings);
    setConfig(merged);
    setPortTexts(buildPortTexts(merged.ports));
  }, [storedOtSettings, systemOtSettings]);

  useEffect(() => {
    const keys = Object.keys(config.protocols_enabled ?? {});
    if (keys.length > 0 && !keys.includes(selectedProtocol)) setSelectedProtocol(keys[0]);
  }, [config.protocols_enabled, selectedProtocol]);

  // ── handlers ──

  const setFiltering = (key, val) =>
    setConfig((c) => ({ ...c, filtering: { ...c.filtering, [key]: val } }));

  const resolvePortFields = (key) => {
    if (PORT_FIELDS[key]) return PORT_FIELDS[key];
    const portKey = `${key}_ports`;
    if (config.ports[portKey] !== undefined || systemOtSettings?.ports?.[portKey] !== undefined)
      return [{ key: portKey, label: 'Ports', type: 'array' }];
    return [];
  };

  const setProtocol = (key, val) => {
    const fields = resolvePortFields(key);
    setConfig((c) => {
      const next = { ...c, protocols_enabled: { ...c.protocols_enabled, [key]: val } };
      if (fields.length > 0 && val) {
        next.ports = { ...next.ports };
        fields.forEach((f) => {
          const hasExisting = f.type === 'array'
            ? Array.isArray(next.ports[f.key]) && next.ports[f.key].length > 0
            : typeof next.ports[f.key] === 'number';
          if (hasExisting) return;
          const sysVal = systemOtSettings?.ports?.[f.key];
          const def    = (Array.isArray(sysVal) && sysVal.length > 0) ? sysVal : DEFAULT_CONFIG.ports[f.key];
          if (f.type === 'array') next.ports[f.key] = Array.isArray(def) ? [...def] : [];
          else if (f.type === 'number' && typeof def === 'number') next.ports[f.key] = def;
        });
      }
      // Disabling a protocol no longer clears its custom ports: they stay in state,
      // simply unused while the protocol is off, and reappear on re-enable.
      return next;
    });
    if (fields.length > 0 && val) {
      setPortTexts((prev) => {
        const next = { ...prev };
        fields.forEach((f) => {
          if (f.type !== 'array') return;
          if (next[f.key] && next[f.key].trim() !== '') return;
          const sysVal = systemOtSettings?.ports?.[f.key];
          const def    = (Array.isArray(sysVal) && sysVal.length > 0) ? sysVal : DEFAULT_CONFIG.ports[f.key];
          next[f.key]  = Array.isArray(def) ? def.join(', ') : '';
        });
        return next;
      });
    }
  };

  const handleSaveConfig = () => {
    const parsedPorts = { ...config.ports };
    Object.entries(portTexts).forEach(([key, text]) => {
      parsedPorts[key] = text.split(',').map((s) => s.trim()).filter(Boolean);
    });
    startToast();
    API.setOtSettings({ ...config, ports: parsedPorts })
      .then(() => updateToastSuccess('Configuration saved successfully'))
      .catch(() => updateToastError('Failed to save configuration'));
  };

  const content = (
    <>
      {/* ── Sidebar (hidden when embedded — DeviceSettings already has one) ── */}
      {!embedded && (
        <Box
          sx={{
            width: 230, flexShrink: 0,
            borderRight: '1px solid #222',
            backgroundColor: '#111',
            py: 1.5,
            display: 'flex', flexDirection: 'column', gap: 0.25,
          }}
        >
          <Typography sx={{ px: 2, pb: 1, fontSize: '0.62rem', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            OT Settings
          </Typography>
          {SECTIONS.map((s) => (
            <SidebarItem key={s.id} section={s} selected={selectedId === s.id} onClick={() => setSelectedId(s.id)} />
          ))}
        </Box>
      )}

      {/* ── Content ─────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, p: 3.5, overflowY: 'auto' }}>
        {selectedId === 'configuration' && (
          <ConfigurationSection
            config={config}
            portTexts={portTexts}
            selectedProtocol={selectedProtocol}
            setSelectedProtocol={setSelectedProtocol}
            setProtocol={setProtocol}
            setFiltering={setFiltering}
            handlePortTextChange={(key, text) => setPortTexts((t) => ({ ...t, [key]: text }))}
            handlePortNumberChange={(key, val) => setConfig((c) => ({ ...c, ports: { ...c.ports, [key]: val } }))}
            effectiveDefault={effectiveDefault}
            storedOtSettings={storedOtSettings}
            systemOtSettings={systemOtSettings}
            onSave={handleSaveConfig}
          />
        )}
      </Box>
    </>
  );

  if (embedded) {
    return <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>{content}</Box>;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        minHeight: 560,
        border: '1px solid rgba(69,144,149,0.15)',
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: 'rgba(20,20,20,0.8)',
      }}
    >
      {content}
    </Paper>
  );
};

export default OtSettings;
